import React, { useState, useEffect } from 'react';
import { X, Search, CheckCircle, Clock, ShieldAlert, User, Mail, Download } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ComplianceDetailsModal = ({ file, onClose }) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'pending', 'acknowledged'

    useEffect(() => {
        fetchDetails();
    }, [file.id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/files/compliance/details/${file.id}`);
            if (response.data.employees) {
                setEmployees(response.data.employees);
            }
        } catch (error) {
            console.error('Error fetching compliance details:', error);
            toast.error('Failed to load compliance details');
        } finally {
            setLoading(false);
        }
    };

    // Filter and search logic
    const filteredEmployees = employees.filter(emp => {
        const matchesSearch =
            emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.position?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter =
            filter === 'all' ||
            (filter === 'pending' && emp.status === 'pending') ||
            (filter === 'acknowledged' && emp.status === 'acknowledged');

        return matchesSearch && matchesFilter;
    });

    const stats = {
        total: employees.length,
        acknowledged: employees.filter(e => e.status === 'acknowledged').length,
        pending: employees.filter(e => e.status === 'pending').length
    };

    const percentage = stats.total > 0 ? Math.round((stats.acknowledged / stats.total) * 100) : 0;

    const handleExport = () => {
        // Simple CSV export
        const headers = ['Name', 'Email', 'Position', 'Status', 'Acknowledged At'];
        const rows = filteredEmployees.map(emp => [
            `${emp.first_name} ${emp.last_name}`,
            emp.email,
            emp.position || 'N/A',
            emp.status,
            emp.acknowledged_at ? new Date(emp.acknowledged_at).toLocaleString() : '-'
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${file.file_name}_compliance_report.csv`;
        link.click();
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh] animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <ShieldAlert size={24} className="text-blue-600" />
                            Compliance Report
                        </h2>
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                            {file.file_name} <span className="text-gray-300">•</span> Version {file.version_number}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Dashboard Stats */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-100">
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex items-center gap-4">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                            <CheckCircle size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Compliance Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{percentage}%</p>
                        </div>
                    </div>
                    <div className="bg-green-50/50 p-4 rounded-xl border border-green-100 flex items-center gap-4">
                        <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                            <User size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Acknowledged</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.acknowledged}<span className="text-sm text-gray-400 font-normal ml-1">/ {stats.total}</span></p>
                        </div>
                    </div>
                    <div className="bg-orange-50/50 p-4 rounded-xl border border-orange-100 flex items-center gap-4">
                        <div className="p-3 bg-orange-100 text-orange-600 rounded-lg">
                            <Clock size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">Pending Review</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                        </div>
                    </div>
                </div>

                {/* Filters & Actions */}
                <div className="p-4 border-b border-gray-100 flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            All Staff
                        </button>
                        <button
                            onClick={() => setFilter('pending')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filter === 'pending' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Pending
                        </button>
                        <button
                            onClick={() => setFilter('acknowledged')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filter === 'acknowledged' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Acknowledged
                        </button>
                    </div>

                    <div className="flex gap-3 flex-1 justify-end">
                        <div className="relative max-w-xs w-full">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search employee..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm"
                            />
                        </div>
                        <button
                            onClick={handleExport}
                            className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium"
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : filteredEmployees.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <Search size={24} className="text-gray-400" />
                            </div>
                            <h3 className="text-gray-900 font-medium mb-1">No employees found</h3>
                            <p className="text-gray-500 text-sm">Try adjusting your filters or search terms.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acknowledged At</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredEmployees.map((emp) => (
                                    <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600 flex items-center justify-center text-xs font-bold ring-2 ring-white shadow-sm">
                                                    {emp.first_name?.[0]}{emp.last_name?.[0]}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{emp.first_name} {emp.last_name}</p>
                                                    <p className="text-xs text-gray-500">{emp.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-600">{emp.position || 'Employee'}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {emp.status === 'acknowledged' ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                                    <CheckCircle size={12} />
                                                    Acknowledged
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                                    <Clock size={12} />
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {emp.acknowledged_at ? (
                                                <span className="text-sm text-gray-600 font-medium">
                                                    {new Date(emp.acknowledged_at).toLocaleDateString()}
                                                    <span className="text-gray-400 ml-1 text-xs">
                                                        {new Date(emp.acknowledged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {emp.status === 'pending' && (
                                                <button
                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                    title="Send Reminder (Coming Soon)"
                                                >
                                                    <Mail size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplianceDetailsModal;
